import {
  GetFragmentDto,
  ListEditsVideoEditorEditsQueryResponse,
} from '@app/modules/fragments/fragment.types';
import { QueriesView } from '@mi-front/ui';

import { SocialExportUploadLayoutSkeleton } from '@app/modules/social-export/publish';
import { SocialExportUploadPageContent } from './SocialExportUploadPageContent';
import {
  type UseSocialExportUploadPageResult,
  useSocialExportUploadPage,
} from './useSocialExportUploadPage';

/** Wires async queries to the social export upload UI for a fragment and platform. */
export function SocialExportUploadPage() {
  const uploadPage: UseSocialExportUploadPageResult = useSocialExportUploadPage();

  if (uploadPage.isResolvingPlatform || !uploadPage.platform) {
    return <SocialExportUploadLayoutSkeleton />;
  }

  if (!uploadPage.areConnectionsResolved) {
    return <SocialExportUploadLayoutSkeleton />;
  }

  if (!uploadPage.selectedConnectionId && uploadPage.connections.length > 0) {
    return <SocialExportUploadLayoutSkeleton />;
  }

  // settingsQuery is intentionally omitted: changing the selected connection refetches
  // settings, and gating the whole page on it would remount into the skeleton.
  // Media URL is optional: deferred uploads can start before assets exist.
  // Empty connections are handled inside content so the platform switcher stays visible.
  return (
    <QueriesView<[GetFragmentDto, ListEditsVideoEditorEditsQueryResponse]>
      loader={<SocialExportUploadLayoutSkeleton />}
      queries={[uploadPage.fragmentQuery, uploadPage.editsListQuery]}
    >
      {([fragment]) => (
        <SocialExportUploadPageContent
          fragment={fragment}
          fragmentMediaUrl={uploadPage.fragmentMediaUrl}
          settingsQuery={uploadPage.settingsQuery}
          platform={uploadPage.platform!}
          selectedSource={uploadPage.selectedSource}
          onSelectedSourceChange={uploadPage.setSelectedSource}
          edits={uploadPage.edits}
          isFragmentSourceAllowed={uploadPage.isFragmentSourceAllowed}
          hasAvailableSources={uploadPage.hasAvailableSources}
          showMediaSelector={uploadPage.showMediaSelector}
          hasSelectedPublishableEdit={uploadPage.hasSelectedPublishableEdit}
          selectedEdit={uploadPage.selectedEdit}
          renderedVideoUrl={uploadPage.renderedVideoUrl}
          PublishForm={uploadPage.PublishForm!}
          onUpload={(data) => uploadPage.handleUpload(fragment, data)}
          connections={uploadPage.connections}
          selectedConnectionId={uploadPage.selectedConnectionId}
          onSelectedConnectionChange={uploadPage.setSelectedConnectionId}
        />
      )}
    </QueriesView>
  );
}
